import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaCopy } from 'react-icons/fa';
const NoteForm = ({ noteType, setNoteType }) => {
    const navigate = useNavigate();
    const [isListening, setIsListening] = useState(false);
    const [currentField, setCurrentField] = useState(null);
    const [diagnosis, setDiagnosis] = useState('');
    const [subjective, setSubjective] = useState('');
    const [objective, setObjective] = useState('');
    const [loading, setLoading] = useState(false);
    const [responseNote, setResponseNote] = useState('Generated content here...');

    const recognition = useMemo(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognitionInstance = new SpeechRecognition();
        recognitionInstance.continuous = true;
        recognitionInstance.interimResults = true;
        recognitionInstance.lang = 'en-US';
        return recognitionInstance;
    }, []);

    const handleListen = useCallback(() => {
        if (isListening) {
            recognition.start();
            recognition.onresult = (event) => {
                let interimTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        if (currentField === 'diagnosis') {
                            setDiagnosis((prev) => prev + transcript + ' ');
                        } else if (currentField === 'subjective') {
                            setSubjective((prev) => prev + transcript + ' ');
                        } else if (currentField === 'objective') {
                            setObjective((prev) => prev + transcript + ' ');
                        }
                    } else {
                        // eslint-disable-next-line no-unused-vars
                        interimTranscript += transcript;
                    }
                }
            };
        } else {
            recognition.stop();
        }
    }, [isListening, currentField, recognition]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const data = { diagnosis, subjective, objective, noteType };

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/generate-note.php`, data);
            setResponseNote(response.data.choices[0].message.content);
        } catch (error) {
            console.error("There was an error generating the note:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const checkSession = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/session-status`, {
                    method: "GET",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                });

                if (response.status === 401) {
                    navigate('/sign-in');
                }
            } catch (error) {
                console.error("Error checking session:", error);
                navigate('/sign-in');
            }
        };

        checkSession();
        handleListen();
        return () => {
            recognition.stop();
        };
    }, [isListening, navigate, handleListen, recognition]);

    const toggleListening = (field) => {
        setCurrentField(field);
        setIsListening((prevState) => !prevState);
    };

    const handleNewNote = (e) => {
        e.preventDefault(); // Prevent form submission if it's within a form
        setDiagnosis(''); // Clear diagnosis field
        setSubjective(''); // Clear subjective field
        setObjective(''); // Clear objective field
        setResponseNote(''); // Clear the generated note content
    };

    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(responseNote)
            .then(() => {
                alert('Note copied to clipboard!');
            })
            .catch((error) => {
                console.error('Error copying text to clipboard:', error);
            });
    };

    return (
        <div className="max-w-3xl mx-auto p-4 sm:p-6 bg-white rounded-lg shadow-md mt-5">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6">Generate Clinical Notes</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <button
                        onClick={handleNewNote}
                        className="p-1 bg-blue-600 text-white font-bold rounded-md hover:bg-blue-700 transition duration-200 float-right text-sm"
                    >
                        Start New Note
                    </button>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Diagnosis:</label>
                    <input
                        type="text"
                        value={diagnosis}
                        onChange={(e) => setDiagnosis(e.target.value)}
                        required
                        placeholder="e.g., Generalized Anxiety Disorder"
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                        type="button"
                        onClick={() => toggleListening('diagnosis')}
                        className="mt-2 p-2 sm:p-3 bg-blue-600 text-white font-bold rounded-md w-full sm:w-auto"
                    >
                        {isListening && currentField === 'diagnosis' ? 'Stop' : 'Listen'}
                    </button>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subjective:</label>
                    <textarea
                        value={subjective}
                        onChange={(e) => setSubjective(e.target.value)}
                        required
                        placeholder="e.g., 40 Year old Hispanic female attended session and reports the following:"
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                        type="button"
                        onClick={() => toggleListening('subjective')}
                        className="mt-2 p-2 sm:p-3 bg-blue-600 text-white font-bold rounded-md w-full sm:w-auto"
                    >
                        {isListening && currentField === 'subjective' ? 'Stop' : 'Listen'}
                    </button>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Objective:</label>
                    <textarea
                        value={objective}
                        onChange={(e) => setObjective(e.target.value)}
                        required
                        placeholder="e.g., Patient appears fidgety and avoids eye contact."
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                        type="button"
                        onClick={() => toggleListening('objective')}
                        className="mt-2 p-2 sm:p-3 bg-blue-600 text-white font-bold rounded-md w-full sm:w-auto"
                    >
                        {isListening && currentField === 'objective' ? 'Stop' : 'Listen'}
                    </button>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Note Type:</label>
                    <select
                        value={noteType}
                        onChange={(e) => setNoteType(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="SOAP">SOAP</option>
                        <option value="BIRP">BIRP</option>
                        <option value="DAP">DAP</option>
                    </select>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full p-3 bg-blue-600 text-white font-bold rounded-md hover:bg-blue-700 transition duration-200"
                >
                    {loading ? 'Generating...' : 'Generate Note'}
                </button>
            </form>

            {responseNote && (
                <div className="mt-6 p-4 bg-gray-100 rounded-md relative">
                    <h3 className="text-xl font-semibold mb-3">Generated Psychology Note:</h3>
                    <FaCopy
                        onClick={handleCopyToClipboard}
                        className="copy-icon"
                        style={{
                            float: 'right',
                            top: '10px',
                            right: '10px',
                            cursor: 'pointer'
                        }}
                    />
                    <pre className="whitespace-pre-wrap">{responseNote}</pre>
                </div>
            )}

            {loading && (
                <div className="spinner"></div>
            )}
        </div>
    );
};

export default NoteForm;
